/**
 * Right-anchored side Drawer (§18 /queue/:orderId). A real modal dialog:
 * role="dialog" aria-modal, a scrim, Esc-to-close, focus moved into the panel on
 * open and restored on close, a lightweight Tab focus-trap, and body-scroll
 * lock. Header (title + meta + close) is fixed; the body scrolls.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "./Button.js";

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function Drawer({
  onClose,
  ariaLabel,
  title,
  meta,
  children,
}: {
  onClose: () => void;
  ariaLabel: string;
  /** Header title node (rendered left). */
  title?: ReactNode;
  /** Optional header meta row under the title. */
  meta?: ReactNode;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Move focus into the panel.
    panel?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab" && panel) {
        const items = focusable(panel);
        if (items.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="drawer-layer">
      <div className="drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <header className="drawer__header">
          <div className="drawer__header-main">
            {title}
            {meta}
          </div>
          <Button variant="ghost" size="sm" iconOnly icon="x" aria-label="Close" onClick={onClose} />
        </header>
        <div className="drawer__body">{children}</div>
      </div>
    </div>
  );
}
